import { byId, inputValue } from "./domUtils";
import { renderAvatarDropdown } from "./avatarView";
import type { PrototypeState } from "./controllerState";
import { refreshHomeState } from "./homeStateController";
import { openModal } from "./navigationController";
import { setLoginPrompt } from './presentAccessDecision';
import { validateEmail, validatePassword, validatePasswordMatch } from "./validation";
import { validateCurrentPassword } from '../../domain/passwordPolicy';
import type { Root } from "./prototypeTypes";
import { syncTopicViewerCacheScope } from './topicViewerCacheScope';
import { DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_MIN_LENGTH } from '../../domain/identityInputPolicy';
import { deactivateHomeMember } from './homeMemberHydrator';

/**
 * Wire the API transport's session-expired callback so that a 401 for a
 * previously-authenticated user (cookie expired / revoked) prompts re-login
 * instead of failing silently in background loaders. Kept here because it
 * is an auth-domain concern; the controller just calls it once at mount.
 */
export function wireSessionExpiredHandler(root: Root, state: PrototypeState) {
  state.runtime.api.onSessionExpired = () => promptSessionReLogin(root, state);
}

/**
 * Shared "this browser's login is gone" UX: settle the visitor state and open
 * the login modal with the standard re-login note. Used both for a mid-flight
 * 401 on a previously-authenticated tab and for a Home hydration the server
 * answered with `sessionRevoked` (stale credential already cleared server-side).
 */
export function promptSessionReLogin(root: Root, state: PrototypeState) {
  showLoginState(root, state);
  openModal(root, "loginModal");
  setLoginPrompt(root, 'Your session has expired. Please sign in again.');
}

export function validateModalLogin(root: Root, failValidation: (message: string) => false) {
  const email = inputValue(root, "loginEmail").trim();
  const password = inputValue(root, "loginPassword");
  const emailError = validateEmail(email);
  if (emailError) return failValidation(emailError);
  const passwordError = validateCurrentPassword(password);
  if (passwordError) return failValidation(passwordError);
  return true;
}

export function validateModalRegister(root: Root, failValidation: (message: string) => false) {
  const name = inputValue(root, "regName").trim();
  const email = inputValue(root, "regEmail").trim();
  const password = inputValue(root, "regPassword");
  const confirm = inputValue(root, "regPasswordConfirm");
  if (name.length < DISPLAY_NAME_MIN_LENGTH || name.length > DISPLAY_NAME_MAX_LENGTH) return failValidation(`Display name must be ${DISPLAY_NAME_MIN_LENGTH} to ${DISPLAY_NAME_MAX_LENGTH} characters.`);
  const emailError = validateEmail(email);
  if (emailError) return failValidation(emailError);
  const pwError = validatePassword(password);
  if (pwError) return failValidation(pwError);
  const matchError = validatePasswordMatch(password, confirm);
  if (matchError) return failValidation(matchError);
  return true;
}

export function hideLoginState(root: Root, state: PrototypeState) {
  state.isLoggedIn = true;
  state.runtime.api?.setAuthenticated(true);
  // round-14: hide the top-nav "Preview account" CTA now
  // that the user is authed. The avatar dropdown becomes
  // the only account entry point.
  byId(root, "navPreviewCta")?.setAttribute("style", "display:none;");
  syncAvatarMenuState(root, state);
  refreshHomeState(root, state);
  emitAuthStateChanged(root);
}

export function showLoginState(root: Root, state: PrototypeState) {
  clearConfirmedLoginState(state);
  // Visitor now sees the app; the legacy full-screen login overlay is
  // only shown when the user explicitly triggers a sign-in flow.
  // round-14: hide "Preview account" for all users (logged-in or not).
  // The avatar dropdown / nav links are the sole account entry points.
  byId(root, "navPreviewCta")?.setAttribute("style", "display:none;");
  syncAvatarMenuState(root, state);
  refreshHomeState(root, state);
  emitAuthStateChanged(root);
}

/**
 * Clear the authenticated controller and cache scope after the server has
 * confirmed logout, without repainting the current page. The caller must
 * immediately replace the document; repainting Profile first briefly exposes
 * visitor-only empty states during the navigation transition.
 */
export function clearConfirmedLoginState(state: PrototypeState) {
	// Home member enrichment waits for its initial inputs before it starts the
	// protected metrics request. Invalidate that deferred branch as soon as
	// this controller becomes a visitor, before navigation can race it.
	deactivateHomeMember();
  state.isLoggedIn = false;
	state.currentPlan = 'visitor';
	state.effectiveAccessPlan = 'visitor';
	state.planContext = null;
	state.runtime.currentPlan = 'visitor';
	// The runtime factory has prototype defaults only before the session is
	// known. Once a real session expires or logout completes, retaining those
	// placeholders would look like a member identity to caches and watermarking.
	state.runtime.userId = '';
	state.runtime.userEmail = '';
	state.runtime.userDisplayName = '';
	state.runtime.subscriptionId = '';
	state.runtime.sessionHash = '';
	syncTopicViewerCacheScope(state.runtime, false);
  state.runtime.api?.setAuthenticated(false);
}

function emitAuthStateChanged(root: Root): void {
	const candidate = root as unknown as { nodeType?: number; ownerDocument?: Document };
	const documentRef = candidate.nodeType === 9 ? root as unknown as Document : candidate.ownerDocument;
	const EventConstructor = documentRef?.defaultView?.Event;
	if (documentRef && EventConstructor) documentRef.dispatchEvent(new EventConstructor('deepmox:auth-state-changed'));
}

export function syncAvatarMenuState(root: Root, state: PrototypeState) {
  // Single render entry — see avatarView.ts header. The previous
  // implementation called ensureAvatarDropdownContent + paintAvatar +
  // paintPlanRow separately, which is the multi-writer pattern that
  // caused the recurring "blank popup" bug. renderAvatarDropdown owns
  // both containers' content and the avatar mark in one pass.
  renderAvatarDropdown(root, state);
}
